@file:Suppress("MagicNumber", "LongMethod")

package com.nonopn.skydome.common.app.pages.projects

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouteCertFoudr
import com.nonopn.skydome.common.app.navigation.RouteCertNsg
import com.nonopn.skydome.common.app.navigation.RouteKeraunicReport
import com.nonopn.skydome.common.app.navigation.RouterCertFoudr
import com.nonopn.skydome.common.app.navigation.RouterCertNsg
import com.nonopn.skydome.common.app.navigation.RouterKeraunicReport
import com.nonopn.skydome.common.app.navigation.RouterMap
import com.nonopn.skydome.common.app.pages.main.model.SpecificData
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.SectionProjectAndThunderDetection
import com.nonopn.skydome.common.app.utils.pad2
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.projects_action_map
import com.nonopn.skywatcher.common.app.res.projects_action_report
import com.nonopn.skywatcher.common.app.res.projects_all_probes
import com.nonopn.skywatcher.common.app.res.projects_count
import com.nonopn.skywatcher.common.app.res.projects_filter_all
import com.nonopn.skywatcher.common.app.res.projects_filter_no_impact
import com.nonopn.skywatcher.common.app.res.projects_no_incidence
import com.nonopn.skywatcher.common.app.res.projects_search_hint
import com.nonopn.skywatcher.common.app.res.projects_created_on
import com.nonopn.skywatcher.common.app.res.projects_sort_created
import com.nonopn.skywatcher.common.app.res.projects_sort_impacts
import com.nonopn.skywatcher.common.app.res.projects_sort_last_alert
import com.nonopn.skywatcher.common.app.res.projects_sort_name
import com.nonopn.skywatcher.common.app.res.projects_summary_1km
import com.nonopn.skywatcher.common.app.res.projects_summary_2km
import com.nonopn.skywatcher.common.app.res.projects_summary_total
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.maps.LatLng

import eu.codlab.compose.widgets.TextNormal
import eu.codlab.viewmodel.rememberViewModel
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import io.ktor.http.Url
import korlibs.time.DateTime
import org.jetbrains.compose.resources.stringResource

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.ln
import kotlin.math.tan

@Composable
fun ProjectsScreen(modifier: Modifier = Modifier) {
    val appModel = LocalApp.current
    val model = rememberViewModel { ProjectsModel(appModel) }
    val state by model.states.collectAsState()
    var probeMenuExpanded by remember { mutableStateOf(false) }
    var sortMenuExpanded by remember { mutableStateOf(false) }
    val gridState = rememberLazyGridState()
    // Consume the pending highlight UUID set by ProbesScreen before navigation
    val highlightUuid = remember { mutableStateOf(appModel.pendingHighlightProjectUuid) }
    LaunchedEffect(Unit) {
        appModel.pendingHighlightProjectUuid?.let {
            highlightUuid.value = it
            appModel.pendingHighlightProjectUuid = null
        }
    }

    Column(modifier = modifier.fillMaxSize().background(AppColor.StrikeBackground)) {
        // ── Summary bar ──────────────────────────────────────────────────────
        if (!state.isLoading) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(AppColor.StrikeCard)
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                SummaryTile(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.projects_summary_total, state.totalProjects),
                    value = state.totalProjects.toString(),
                    valueColor = AppColor.StrikePrimary,
                )
                SummaryTile(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.projects_summary_1km),
                    value = state.totalImpacts1Km.toString(),
                    valueColor = Colors.cgPositive,
                )
                SummaryTile(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.projects_summary_2km),
                    value = state.totalImpacts2Km.toString(),
                    valueColor = Colors.incidenceMid,
                )
            }
            HorizontalDivider(color = AppColor.StrikeCardBackground)
        }

        // ── Search bar ──────────────────────────────────────────────────────
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppColor.StrikeCard)
                .padding(horizontal = 12.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            OutlinedTextField(
                modifier = Modifier.weight(1f).height(48.dp),
                value = state.searchQuery,
                onValueChange = { model.setSearchQuery(it) },
                placeholder = { TextNormal(stringResource(Res.string.projects_search_hint), fontSize = 13.sp, color = AppColor.StrikeText.copy(alpha = 0.4f)) },
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AppColor.StrikePrimary,
                    unfocusedBorderColor = AppColor.StrikeCardBackground,
                    focusedTextColor = AppColor.StrikeText,
                    unfocusedTextColor = AppColor.StrikeText,
                ),
            )
            IconButton(
                onClick = { /* search is live */ },
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(AppColor.StrikePrimary),
            ) {
                Icon(
                    imageVector = Icons.Default.Search,
                    contentDescription = "Search",
                    tint = Color.White,
                )
            }
        }

        // ── Filter bar (probes | project filters | sort) ────────────────────
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppColor.StrikeCard)
                .padding(horizontal = 12.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // ── Probes section ──
            val allProbesLabel = stringResource(Res.string.projects_all_probes)
            val probeName = state.probes.firstOrNull { it.uuid == state.selectedProbeUuid }
                ?.let { it.name.ifBlank { "Probe #1" } } ?: allProbesLabel
            Box {
                FilterPill(text = "📡 $probeName", selected = state.selectedProbeUuid != null) {
                    probeMenuExpanded = true
                }
                DropdownMenu(
                    expanded = probeMenuExpanded,
                    onDismissRequest = { probeMenuExpanded = false },
                    modifier = Modifier
                        .background(AppColor.StrikeCardBackground, RoundedCornerShape(12.dp))
                        .heightIn(max = 300.dp),
                    containerColor = AppColor.StrikeCardBackground,
                ) {
                    DropdownMenuItem(
                        text = {
                            TextNormal(
                                "📡 $allProbesLabel",
                                color = AppColor.StrikeText,
                                fontWeight = if (state.selectedProbeUuid == null) FontWeight.SemiBold else FontWeight.Normal,
                            )
                        },
                        onClick = { model.setProbeFilter(null); probeMenuExpanded = false },
                    )
                    HorizontalDivider(color = AppColor.StrikeText.copy(alpha = 0.12f))
                    state.probes.forEachIndexed { idx, p ->
                        if (idx > 0) HorizontalDivider(color = AppColor.StrikeText.copy(alpha = 0.08f))
                        DropdownMenuItem(
                            text = {
                                TextNormal(
                                    "📡 ${p.name.ifBlank { "Probe #${idx + 1}" }}",
                                    color = AppColor.StrikeText,
                                    fontWeight = if (p.uuid == state.selectedProbeUuid) FontWeight.SemiBold else FontWeight.Normal,
                                )
                            },
                            onClick = { model.setProbeFilter(p.uuid); probeMenuExpanded = false },
                        )
                    }
                }
            }
            // ── Separator ──
            Box(Modifier.width(1.dp).height(24.dp).background(AppColor.StrikeCardBackground))
            // ── Project filters (Tous / Sans impact) ──
            val labelAll = stringResource(Res.string.projects_filter_all)
            val labelNoImpact = stringResource(Res.string.projects_filter_no_impact)
            FilterPill(text = labelAll, selected = state.incidenceFilter == ProjectsIncidenceFilter.ALL) {
                model.setIncidenceFilter(ProjectsIncidenceFilter.ALL)
            }
            FilterPill(text = labelNoImpact, selected = state.incidenceFilter == ProjectsIncidenceFilter.NO_IMPACT) {
                model.setIncidenceFilter(ProjectsIncidenceFilter.NO_IMPACT)
            }
            // ── Separator ──
            Box(Modifier.width(1.dp).height(24.dp).background(AppColor.StrikeCardBackground))
            // ── Sort ──
            val sortLabels = mapOf(
                ProjectsSortOrder.LAST_ALERT to stringResource(Res.string.projects_sort_last_alert),
                ProjectsSortOrder.NAME to stringResource(Res.string.projects_sort_name),
                ProjectsSortOrder.IMPACTS to stringResource(Res.string.projects_sort_impacts),
                ProjectsSortOrder.CREATED to stringResource(Res.string.projects_sort_created),
            )
            Box {
                FilterPill(text = sortLabels[state.sortOrder] ?: "Sort", selected = true) {
                    sortMenuExpanded = true
                }
                DropdownMenu(
                    expanded = sortMenuExpanded,
                    onDismissRequest = { sortMenuExpanded = false },
                    modifier = Modifier.background(AppColor.StrikeCardBackground, RoundedCornerShape(12.dp)),
                    containerColor = AppColor.StrikeCardBackground,
                ) {
                    ProjectsSortOrder.entries.forEach { order ->
                        DropdownMenuItem(
                            text = {
                                TextNormal(
                                    sortLabels[order] ?: "",
                                    color = AppColor.StrikeText,
                                    fontWeight = if (state.sortOrder == order) FontWeight.SemiBold else FontWeight.Normal,
                                )
                            },
                            onClick = { model.setSortOrder(order); sortMenuExpanded = false },
                        )
                    }
                }
            }
        }
        HorizontalDivider(color = AppColor.StrikeCardBackground)

        // ── Content ──────────────────────────────────────────────────────────
        when {
            state.isLoading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AppColor.StrikePrimary)
            }
            state.error != null -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                TextNormal(state.error ?: "", color = AppColor.StrikeDanger)
            }
            else -> {
                val items = state.filtered
                // Track the exact index of the target project so scroll re-fires after phase-2 re-sort
                val targetIndex = if (highlightUuid.value != null)
                    items.indexOfFirst { it.project.uuid == highlightUuid.value }
                else -1
                LaunchedEffect(targetIndex) {
                    if (targetIndex >= 0) gridState.animateScrollToItem(targetIndex)
                }
                TextNormal(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 6.dp),
                    text = stringResource(Res.string.projects_count, items.size),
                    fontSize = 12.sp,
                    color = AppColor.StrikeText.copy(alpha = 0.5f),
                )
                LazyVerticalGrid(
                    state = gridState,
                    columns = GridCells.Fixed(2),
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(8.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    items(items) { item ->
                        ProjectCard(
                            item = item,
                            isHighlighted = item.project.uuid == highlightUuid.value,
                            onRapport = {
                                appModel.show(
                                    RouterKeraunicReport.navigateTo(
                                        RouteKeraunicReport(
                                            projectUuid = item.project.uuid,
                                            projectName = item.project.name,
                                            lat = item.project.latitude,
                                            lon = item.project.longitude,
                                        )
                                    )
                                )
                            },
                            onNsg = {
                                appModel.show(
                                    RouterCertNsg.navigateTo(
                                        RouteCertNsg(
                                            projectUuid = item.project.uuid,
                                            projectName = item.project.name,
                                            lat = item.project.latitude,
                                            lon = item.project.longitude,
                                        )
                                    )
                                )
                            },
                            onCertFoudr = { top ->
                                appModel.show(
                                    RouterCertFoudr.navigateTo(
                                        RouteCertFoudr(
                                            projectUuid = item.project.uuid,
                                            projectName = item.project.name,
                                            projectLat = item.project.latitude,
                                            projectLon = item.project.longitude,
                                            impactLat = top.activity.latitude,
                                            impactLon = top.activity.longitude,
                                            impactTimestamp = top.activity.recTimestampSeconds,
                                            impactAmplitude = top.activity.pulsePeakAmp,
                                            impactPercent = top.percent,
                                            impactExternalId = top.activity.externalId,
                                        )
                                    )
                                )
                            },
                            onMap = {
                                appModel.writeSpecificData(
                                    SpecificData(
                                        coordinates = LatLng(item.project.latitude, item.project.longitude),
                                        zoom = 0.1
                                    )
                                )
                                appModel.show(RouterMap.navigateTo())
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ProjectCard(
    item: ProjectWithStats,
    isHighlighted: Boolean = false,
    onRapport: () -> Unit = {},
    onNsg: () -> Unit = {},
    onCertFoudr: (SectionProjectAndThunderDetection.Top) -> Unit = {},
    onMap: () -> Unit = {},
) {

    // OSM tile URL for this project location
    val zoom = 13
    val n = (1 shl zoom).toDouble()
    val xTile = floor((item.project.longitude + 180.0) / 360.0 * n).toInt()
    val latRad = item.project.latitude * PI / 180.0
    val yTile = floor((1.0 - ln(tan(latRad) + 1.0 / cos(latRad)) / PI) / 2.0 * n).toInt()
    val tileUrl = "https://api.lpsmanager.io/map/tile/$zoom/$xTile/$yTile"

    // Days since last alert
    val nowSec = DateTime.now().unixMillisLong / 1000L
    val diffDays = if (item.lastAlertSeconds > 0) (nowSec - item.lastAlertSeconds) / 86400 else -1L

    val highlightModifier = if (isHighlighted) {
        Modifier.border(2.dp, AppColor.StrikePrimary, RoundedCornerShape(8.dp))
    } else Modifier
    Column(modifier = Modifier.fillMaxWidth().then(highlightModifier).background(AppColor.StrikeCard)) {
        // ── Header ────────────────────────────────────────────────────────
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.Top,
        ) {
            // Mini OSM map (non-clickable for toggle)
            Box(
                modifier = Modifier.size(100.dp).clip(RoundedCornerShape(8.dp))
                    .border(1.dp, AppColor.StrikePrimary.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
                    .background(AppColor.StrikeBackground),
                contentAlignment = Alignment.Center,
            ) {
                KamelImage(
                    modifier = Modifier.fillMaxSize(),
                    resource = asyncPainterResource(data = Url(tileUrl)),
                    contentScale = ContentScale.Crop,
                    contentDescription = "Map",
                    onLoading = { CircularProgressIndicator(Modifier.size(16.dp), color = AppColor.StrikePrimary, strokeWidth = 2.dp) },
                    onFailure = { TextNormal("🗺️", fontSize = 22.sp) },
                )
                Box(Modifier.size(8.dp).background(AppColor.StrikePrimary, RoundedCornerShape(50)))
            }

            // Project info
            Column(modifier = Modifier.weight(1f)) {
                TextNormal(item.project.name, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = AppColor.StrikeText)
                item.project.createdAt?.let { createdAt ->
                    val date = DateTime(createdAt)
                    TextNormal(
                        stringResource(
                            Res.string.projects_created_on,
                            "${date.dayOfMonth.pad2()}/${date.month1.pad2()}/${date.yearInt}"
                        ),
                        fontSize = 11.sp,
                        color = AppColor.StrikeText.copy(alpha = 0.6f)
                    )
                }
                if (item.nearestProbe != null && item.nearestProbeDistanceKm <= 100.0) {
                    Spacer(Modifier.height(4.dp))
                    val probeName = item.nearestProbe.name
                        .takeIf { it.isNotBlank() && !it.contains("-") }
                        ?: "Sonde"
                    Box(
                        Modifier.clip(RoundedCornerShape(6.dp))
                            .background(AppColor.StrikePrimary.copy(alpha = 0.12f))
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        TextNormal(
                            "📡 $probeName · ${formatDistKm(item.nearestProbeDistanceKm)}",
                            fontSize = 11.sp,
                            color = AppColor.StrikePrimary,
                        )
                    }
                }
                Spacer(Modifier.height(6.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    ImpactStat("< 1 km", item.count1Km)
                    ImpactStat("< 2 km", item.count2Km)
                }
            }

            // Right column: alert badge + score badge + toggle
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                if (diffDays in 0..364) {
                    Box(
                        Modifier.clip(RoundedCornerShape(6.dp))
                            .background(Colors.incidenceMid.copy(alpha = 0.15f))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        TextNormal(
                            "⚡ ${if (diffDays == 0L) "< 1 j" else "$diffDays j"}",
                            fontSize = 11.sp,
                            color = Colors.incidenceMid,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
                val scoreColor = when {
                    item.score < 50 -> Colors.cgPositive
                    item.score < 80 -> Colors.incidenceMid
                    else -> Colors.incidenceLow
                }
                Box(
                    Modifier.clip(RoundedCornerShape(6.dp))
                        .background(scoreColor.copy(alpha = 0.12f))
                        .padding(horizontal = 8.dp, vertical = 3.dp)
                ) {
                    TextNormal(
                        "Score ${item.score}%",
                        fontSize = 11.sp,
                        color = scoreColor,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }

        // ── Body ──────────────────────────────────────────────────────────
        Column(modifier = Modifier.fillMaxWidth()) {
            HorizontalDivider(color = AppColor.StrikeCardBackground)

                // Zone stats (avg / max by polarity within 1 km and 2 km)
                ZoneStatsSection(stats1Km = item.stats1Km, stats2Km = item.stats2Km)
                HorizontalDivider(color = AppColor.StrikeCardBackground)

                // Top 5 incidences
                if (item.top5.isNotEmpty()) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        TextNormal(
                            "⚡ Top ${item.top5.size} incidences",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = AppColor.StrikeText.copy(alpha = 0.7f),
                        )
                        Spacer(Modifier.height(2.dp))
                        item.top5.forEachIndexed { idx, top ->
                            val amp = top.activity.pulsePeakAmp
                            val kA = abs(amp) / 1000
                            val ampColor = if (amp > 0) Colors.cgPositive else Colors.cgNegative
                            val polarity = if (amp > 0) "CG+" else "CG−"
                            val pct = top.percent.toInt()
                            val pctColor = when {
                                pct >= 80 -> Colors.cgPositive
                                pct >= 30 -> Colors.incidenceMid
                                else -> Colors.incidenceLow
                            }
                            // Date/time from recDateTime (local time)
                            val dt = top.activity.recDateTime
                            val day = dt.dayOfMonth.toString().padStart(2, '0')
                            val mon = dt.month1.toString().padStart(2, '0')
                            val yr = dt.yearInt.toString()
                            val hr = dt.hours.toString().padStart(2, '0')
                            val min = dt.minutes.toString().padStart(2, '0')
                            val dateLabel = "$day/$mon/$yr"
                            val timeLabel = "$hr:$min"
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                // Left: index + amplitude + badge
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    TextNormal(
                                        "${idx + 1}.",
                                        fontSize = 11.sp,
                                        color = AppColor.StrikeText.copy(alpha = 0.4f),
                                    )
                                    TextNormal(
                                        "⚡ $kA kA $polarity",
                                        fontSize = 12.sp,
                                        color = ampColor,
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                    Box(
                                        Modifier.clip(RoundedCornerShape(4.dp))
                                            .background(pctColor.copy(alpha = 0.12f))
                                            .padding(horizontal = 5.dp, vertical = 1.dp)
                                    ) {
                                        TextNormal("$pct%", fontSize = 11.sp, color = pctColor, fontWeight = FontWeight.Medium)
                                    }
                                }
                                // Right: date + time + certif button
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Column(horizontalAlignment = Alignment.End) {
                                        TextNormal(
                                            dateLabel,
                                            fontSize = 11.sp,
                                            color = AppColor.StrikeText.copy(alpha = 0.55f),
                                        )
                                        TextNormal(
                                            timeLabel,
                                            fontSize = 11.sp,
                                            color = AppColor.StrikeText.copy(alpha = 0.4f),
                                        )
                                    }
                                    Box(
                                        modifier = Modifier
                                            .clip(RoundedCornerShape(12.dp))
                                            .border(1.dp, Colors.incidenceMid.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
                                            .background(Colors.incidenceMid.copy(alpha = 0.08f))
                                            .clickable { onCertFoudr(top) }
                                            .padding(horizontal = 7.dp, vertical = 3.dp),
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        TextNormal(
                                            "⚡ Certif.",
                                            fontSize = 10.sp,
                                            color = Colors.incidenceMid,
                                            fontWeight = FontWeight.Medium,
                                        )
                                    }
                                }
                            }
                        }
                    }
                } else {
                    TextNormal(
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                        text = stringResource(Res.string.projects_no_incidence),
                        fontSize = 12.sp,
                        color = Colors.incidenceLow,
                    )
                }

                // ── Action buttons ────────────────────────────────────────
                HorizontalDivider(color = AppColor.StrikeCardBackground)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppColor.StrikeBackground.copy(alpha = 0.5f))
                        .padding(horizontal = 10.dp, vertical = 8.dp),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    ActionButton(text = stringResource(Res.string.projects_action_report), color = AppColor.StrikePrimary) { onRapport() }
                    ActionButton(text = "🌩️ Nsg", color = Colors.incidenceLow) { onNsg() }
                    Spacer(Modifier.weight(1f))
                    ActionButton(text = stringResource(Res.string.projects_action_map), color = AppColor.StrikeText.copy(alpha = 0.6f)) { onMap() }
                }
        }
    }
}

@Composable
private fun ZoneStatsSection(stats1Km: ZoneStats, stats2Km: ZoneStats) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TextNormal(
            "📊 Intensités · 12 mois",
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
            color = AppColor.StrikeText.copy(alpha = 0.6f),
        )
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            ZoneStatsCard(modifier = Modifier.weight(1f), label = "< 1 km", stats = stats1Km)
            ZoneStatsCard(modifier = Modifier.weight(1f), label = "1-2 km", stats = stats2Km)
        }
    }
}

@Composable
private fun ZoneStatsCard(modifier: Modifier = Modifier, label: String, stats: ZoneStats) {
    val hasDetail = stats.avgNegKa > 0.0 || stats.avgPosKa > 0.0
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(AppColor.StrikeBackground)
            .padding(horizontal = 8.dp, vertical = 6.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        TextNormal(label, fontSize = 12.sp, fontWeight = FontWeight.Bold, color = AppColor.StrikeText)
        if (hasDetail) {
            PolarityStatRow(
                polarity = "CG−",
                color = Colors.cgNegative,
                count = stats.countNeg,
                avg = stats.avgNegKa,
                max = stats.maxNegKa,
                maxDate = stats.maxNegDate,
            )
            PolarityStatRow(
                polarity = "CG+",
                color = Colors.cgPositive,
                count = stats.countPos,
                avg = stats.avgPosKa,
                max = stats.maxPosKa,
                maxDate = stats.maxPosDate,
            )
            val known = stats.countNeg + stats.countPos
            if (stats.totalCount > known) {
                TextNormal(
                    "${stats.totalCount} impacts",
                    fontSize = 10.sp,
                    color = AppColor.StrikeText.copy(alpha = 0.5f),
                )
            }
        } else if (stats.totalCount > 0) {
            TextNormal(
                "${stats.totalCount} impacts",
                fontSize = 11.sp,
                color = AppColor.StrikeText.copy(alpha = 0.7f),
            )
        } else {
            TextNormal("—", fontSize = 11.sp, color = AppColor.StrikeText.copy(alpha = 0.4f))
        }
    }
}

@Composable
private fun PolarityStatRow(polarity: String, color: Color, count: Int = 0, avg: Double, max: Double, maxDate: String = "") {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        verticalAlignment = Alignment.Top,
    ) {
        val label = if (count > 0) "$polarity ($count)" else polarity
        TextNormal(label, fontSize = 11.sp, color = color, fontWeight = FontWeight.Medium)
        if (avg == 0.0 && max == 0.0) {
            TextNormal("—", fontSize = 11.sp, color = AppColor.StrikeText.copy(alpha = 0.4f))
        } else {
            Column {
                TextNormal(
                    "moy ${formatKa(avg)} · max ${formatKa(max)} kA",
                    fontSize = 11.sp,
                    color = AppColor.StrikeText.copy(alpha = 0.7f),
                )
                if (maxDate.isNotEmpty()) {
                    TextNormal(
                        maxDate,
                        fontSize = 10.sp,
                        color = AppColor.StrikeText.copy(alpha = 0.4f),
                    )
                }
            }
        }
    }
}

private fun formatKa(ka: Double): String {
    if (ka == 0.0) return "—"
    val tenths = (ka * 10).toInt()
    return "${tenths / 10},${tenths % 10}"
}

@Composable
private fun SummaryTile(modifier: Modifier = Modifier, label: String, value: String, valueColor: Color) {
    Column(
        modifier = modifier.padding(vertical = 4.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        TextNormal(value, fontSize = 22.sp, fontWeight = FontWeight.ExtraBold, color = valueColor)
        TextNormal(label, fontSize = 11.sp, color = AppColor.StrikeText.copy(alpha = 0.55f))
    }
}

@Composable
private fun ImpactStat(label: String, count: Int) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        TextNormal(count.toString(), fontSize = 16.sp, fontWeight = FontWeight.ExtraBold, color = AppColor.StrikePrimary)
        TextNormal(label, fontSize = 10.sp, color = AppColor.StrikeText.copy(alpha = 0.55f))
    }
}

private fun formatDistKm(km: Double): String {
    if (km < 1.0) return "<1 km"
    val intPart = km.toInt()
    val dec = kotlin.math.round((km - intPart) * 10).toInt()
    return "$intPart,$dec km"
}

@Composable
private fun ActionButton(text: String, color: Color, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .border(1.dp, color.copy(alpha = 0.4f), RoundedCornerShape(16.dp))
            .background(color.copy(alpha = 0.08f))
            .clickable { onClick() }
            .padding(horizontal = 10.dp, vertical = 5.dp),
        contentAlignment = Alignment.Center,
    ) { TextNormal(text, fontSize = 12.sp, color = color, fontWeight = FontWeight.Medium) }
}

@Composable
private fun FilterPill(text: String, selected: Boolean, onClick: () -> Unit) {
    val bg = if (selected) AppColor.StrikePrimary else AppColor.StrikePrimary.copy(alpha = 0.1f)
    val textColor = if (selected) Color.White else AppColor.StrikePrimary
    Box(
        modifier = Modifier.clip(RoundedCornerShape(20.dp))
            .border(1.dp, AppColor.StrikePrimary, RoundedCornerShape(20.dp))
            .background(bg).clickable { onClick() }
            .padding(horizontal = 12.dp, vertical = 5.dp),
        contentAlignment = Alignment.Center,
    ) { TextNormal(text, fontSize = 12.sp, color = textColor, fontWeight = FontWeight.Medium) }
}

private object Colors {
    val cgNegative = Color(0xFF2B7FE0)
    val cgPositive = Color(0xFFE63B2E)
    val incidenceMid = Color(0xFFF5A800)
    val incidenceLow = Color(0xFF2E7D32)
}
